import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, ShieldAlert } from 'lucide-react';
import { formatUsd } from '@ach/shared';

import { api } from '../lib/api.js';
import { useAdmin } from './useAdminSession.js';
import { Spinner, ErrorNote, WarningNote, EmptyState, StatusBadge, InfoNote } from './components.js';

interface Row {
  id: string;
  amount_cents: number;
  submitted_at: string;
  bank_name: string;
  account_type: 'checking' | 'savings';
  account_last4: string;
  payment_request_id: string;
  status: 'submitted' | 'recorded';
  recorded_at: string | null;
  customer_name: string;
  invoice_numbers: string | null;
}

export function Submissions() {
  const { me } = useAdmin();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [grouping, setGrouping] = useState<'per-invoice' | 'combined'>('per-invoice');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ submissions: Row[] }>('/api/admin/submissions')
      .then((data) => setRows(data.submissions))
      .catch(() => setError('Could not load submissions.'));
  }, []);

  function toggle(id: string) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /**
   * Downloads the CSV.
   *
   * Uses fetch rather than a plain link because the route is a POST behind a
   * bearer token, and because a failure needs to surface as a readable message
   * rather than a browser download of a JSON error body.
   */
  async function exportCsv() {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/export/bank-csv', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ submissionIds: [...selected], grouping }),
      });

      if (!response.ok) {
        const detail = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(detail.error ?? `Export failed (${response.status}).`);
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        response.headers.get('content-disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'ach.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not export.');
    } finally {
      setBusy(false);
    }
  }

  if (error && !rows) return <ErrorNote>{error}</ErrorNote>;
  if (!rows) return <Spinner label="Loading submissions" />;

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold text-slate-900">Submissions</h1>

      {!me.canRevealBankDetails && (
        <WarningNote>
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div>
              This session signed in without your authenticator code, so the bank CSV cannot be
              exported. Sign out and back in with the code.
            </div>
          </div>
        </WarningNote>
      )}

      {error && <ErrorNote>
        <pre className="whitespace-pre-wrap font-sans">{error}</pre>
      </ErrorNote>}

      {rows.length === 0 ? (
        <EmptyState title="No submissions yet">
          They appear here once a customer has submitted their bank details.
        </EmptyState>
      ) : (
        <>
          <div className="card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="w-10 px-4 py-2"></th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <th className="px-4 py-2 font-medium">Invoices</th>
                  <th className="px-4 py-2 font-medium">Account</th>
                  <th className="px-4 py-2 font-medium">Submitted</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className={selected.has(row.id) ? 'bg-gold-50' : ''}>
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-gold-600 focus:ring-gold-500"
                        checked={selected.has(row.id)}
                        onChange={() => toggle(row.id)}
                        aria-label={`Select ${row.customer_name}`}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/requests/${row.payment_request_id}`}
                        className="font-medium text-gold-800 hover:underline"
                      >
                        {row.customer_name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{row.invoice_numbers ?? '—'}</td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {row.bank_name}{' '}
                      <span className="font-account text-slate-500">
                        {row.account_type === 'savings' ? 'SV' : 'CH'} ••••{row.account_last4}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">
                      {new Date(row.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-slate-900">
                      {formatUsd(row.amount_cents)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card flex flex-wrap items-center gap-3 p-4">
            <span className="text-sm text-slate-600">
              {selected.size} selected
              {selected.size > 0 && (
                <>
                  {' — '}
                  {formatUsd(
                    rows
                      .filter((row) => selected.has(row.id))
                      .reduce((sum, row) => sum + row.amount_cents, 0),
                  )}
                </>
              )}
            </span>

            <label className="ml-auto flex items-center gap-2 text-sm text-slate-600">
              Rows
              <select
                className="field-input w-auto py-1.5"
                value={grouping}
                onChange={(e) => setGrouping(e.target.value as 'per-invoice' | 'combined')}
              >
                <option value="per-invoice">One per invoice</option>
                <option value="combined">One per customer</option>
              </select>
            </label>

            <button
              className="btn-primary"
              onClick={() => void exportCsv()}
              disabled={busy || selected.size === 0 || !me.canRevealBankDetails}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Download className="h-4 w-4" aria-hidden />
              )}
              Export bank CSV
            </button>
          </div>

          <InfoNote>
            The file matches what E-Check Pro produces —{' '}
            <code className="font-account text-xs">
              "Customer",Order,Routing,Account,Paid,"CH","DB"
            </code>{' '}
            with no header row and CRLF endings — so it drops straight into the bank upload.{' '}
            <strong>One row per invoice</strong> keys each line to a Printer's Plan invoice number
            but means several debits on the customer's statement;{' '}
            <strong>one per customer</strong> is a single debit. Exporting is written to the access
            log.
          </InfoNote>
        </>
      )}
    </div>
  );
}
